import { Product, ProductVariant } from "../types/api";

/**
 * Cleans a single variant option value before it is stored. `filterValues` is
 * persisted as a comma-joined string, so a comma inside a value would be read
 * back as an extra value (and break the Shopify sync). Commas are silently
 * removed, whitespace runs collapsed and the ends trimmed, e.g.
 * "Light, 12-52 " becomes "Light 12-52".
 */
export const sanitiseFilterValue = (value: unknown): string =>
  (value ?? "").toString().replace(/,/g, " ").replace(/\s+/g, " ").trim();

/**
 * Builds a normalised key representing a variant's combination of filter
 * values. Values are trimmed and lower-cased so that trivial differences
 * (whitespace/case) are still treated as duplicates, and joined with a
 * separator that cannot occur in a value so the combination is unambiguous.
 *
 * `filterValues` is sometimes an array and sometimes a comma-joined string at
 * runtime, so both shapes are handled here.
 */
const filterValuesKey = (variant: ProductVariant): string => {
  const values = Array.isArray(variant.filterValues)
    ? variant.filterValues
    : variant.filterValues
    ? String(variant.filterValues).split(",")
    : [];

  return values
    .map((value) => (value ?? "").toString().trim().toLowerCase())
    .join("\0");
};

/**
 * Returns true when two or more (non-deleted) variants share the same
 * combination of filter values.
 *
 * A product's variants must each have a unique combination of variant option
 * values (e.g. for a single "Colour" option, [Red, Blue, Blue] is invalid; for
 * two options [{1, Blue}, {1, Blue}] is invalid but [{1, Blue}, {2, Blue}] is
 * fine). Shopify enforces this, so a product in this state cannot be published.
 */
export const hasDuplicateFilterValues = (product: Product): boolean => {
  const activeVariants = (product?.variants || []).filter(
    (v: any) => !v.pendingDelete
  );

  const keys = activeVariants.map(filterValuesKey);
  return new Set(keys).size !== keys.length;
};
